/**
 * MergeMenu — Blender-style merge option popup.
 *
 * Shown when pressing M in edit mode with 2+ vertices selected in vertex
 * granularity. "By Distance" now enters the shared modal command flow so
 * preview, confirm, and Adjust Last Operation all use the same path.
 */

import { useCallback } from "react";
import { useStore } from "../app/store.ts";
import type { MergeStrategy } from "../modeling/meshOps.ts";
import type { CommandMenuOption } from "./FloatingCommandMenu.tsx";
import { FloatingCommandMenu } from "./FloatingCommandMenu.tsx";
import { useOperatorMenu } from "./hooks/useOperatorMenu.ts";

const OPTIONS: CommandMenuOption[] = [
  { id: "center", label: "At Center", shortcutKey: "c" },
  { id: "first", label: "At First", shortcutKey: "f" },
  { id: "last", label: "At Last", shortcutKey: "l" },
  { id: "by_distance", label: "By Distance", shortcutKey: "d" },
];

export function MergeMenu() {
  const menu = useOperatorMenu("merge");
  const mergeSelection = useStore((s) => s.mergeSelection);
  const mergeByDistanceSelection = useStore((s) => s.mergeByDistanceSelection);

  const close = useCallback(() => {
    menu.close();
  }, [menu]);

  const handleSelect = useCallback(
    (id: string) => {
      if (id === "by_distance") {
        mergeByDistanceSelection();
      } else {
        mergeSelection(id as MergeStrategy);
      }
      close();
    },
    [close, mergeByDistanceSelection, mergeSelection],
  );

  return (
    <FloatingCommandMenu
      open={menu.open}
      onClose={close}
      title="Merge"
      width={180}
      anchorPoint={menu.anchorPoint}
      options={OPTIONS}
      onSelect={handleSelect}
      panelKey="merge"
    />
  );
}
